import { library } from '@fortawesome/fontawesome-svg-core';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { action, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_WarningFeedback } from '../../../../design/feedback/warningFeedback/S_WarningFeedback';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { buildBounds, getLatLngsForGeojson } from '../../../helpers/geometry/polygon.helpers';
import { InputStatus } from '../../../models/error.model';
import { IAnnotationService } from '../annotation.service';
import { IAnnotationsStore } from '../annotations.store';
import { IAnnotationUiStore } from '../annotationUi.store';
import { IFreeDrawSegmentationService } from '../freeDrawSegmentation.service';
import { cleanClones, validateQuestion } from '../question.model';
import { IAnnotationTypeBl } from '../submodules/annotationTypes/annotationType.bl';
import { ChangeSegmentationWithAnswersCommand } from '../undoRedoCommands/changeSegmentationWithAnswersCommand';
import { SegmentationAnswerAddedCommand } from '../undoRedoCommands/segmentationAnswerAddedCommand';
import { IUndoRedoHistory } from '../undoRedoHistory.service';

library.add(faExclamationTriangle);

export const SegmentationsSaveContainer = observer((): ReactElement => {
  const annotationService = useInjection(IAnnotationService.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const uiStore = useInjection(IAnnotationUiStore.$);
  const undoRedoHistory = useInjection(IUndoRedoHistory.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);

  const { t } = useTranslator();

  const handleSaveAndShowNext = action(async (): Promise<void> => {
    await handleSave();
    if (!annotationsStore.selectedSegmentation) {
      annotationService.moveToNextInvalidSegmentation();
    }
  });

  const handleSave = action(async (): Promise<void> => {
    if (freeDrawSegmentationService.freeDrawFeature) {
      await handleFreeDrawSave();
    } else {
      await handleRegularDrawSave();
    }
  });

  const handleFreeDrawSave = async (): Promise<void> => {
    const feature = freeDrawSegmentationService.freeDrawFeature;
    const segmentation = feature ? annotationsStore.segmentations.find((s) => s.feature.id === feature.id) : undefined;

    const newQuestions = cleanClones(annotationsStore.currentQuestions);

    if (!feature || !feature.geometry.coordinates.length) {
      if (segmentation) {
        segmentation.questions = newQuestions;
        const command = new ChangeSegmentationWithAnswersCommand(annotationService, annotationsStore, freeDrawSegmentationService, segmentation, undefined);

        annotationService.deleteSegmentation(segmentation.feature.id);
        undoRedoHistory.addCommand(command);
      }
      await secureClear();
      return;
    }

    annotationsStore.currentQuestions.forEach((q) => validateQuestion(q));
    if (annotationsStore.currentQuestions.filter((q) => q.status.isValid === false).length > 0) {
      return;
    }

    const latlngs = getLatLngsForGeojson(feature);

    const editedSegmentation = {
      feature,
      latlngs,
      questions: annotationsStore.currentQuestions.map((x) => {
        return { ...toJS(x), status: InputStatus.empty() };
      }),
      bbox: buildBounds(latlngs),
      priority: segmentation !== undefined ? segmentation.priority : 0,
    };

    const command = new ChangeSegmentationWithAnswersCommand(
      annotationService,
      annotationsStore,
      freeDrawSegmentationService,
      segmentation,
      editedSegmentation
    );

    if (!segmentation) {
      annotationService.addNewSegmentation(editedSegmentation);
    } else {
      segmentation.feature = editedSegmentation.feature;
      segmentation.questions = editedSegmentation.questions;
      segmentation.latlngs = editedSegmentation.latlngs;
      segmentation.bbox = editedSegmentation.bbox;
      segmentation.questions = annotationsStore.currentQuestions.map((x) => {
        return { ...toJS(x), status: InputStatus.empty() };
      });
    }

    undoRedoHistory.addCommand(command);

    await clear();
  };

  const handleRegularDrawSave = async (): Promise<void> => {
    const feature = annotationsStore.selectedSegmentation?.feature;
    const segmentation = feature ? annotationsStore.segmentations.find((s) => s.feature.id === feature.id) : undefined;

    if (!segmentation || !feature || !feature.geometry.coordinates.length) {
      if (segmentation) {
        annotationService.deleteSegmentation(segmentation.feature.id);
      }
      await clear();
      return;
    }

    annotationsStore.currentQuestions.forEach((q) => validateQuestion(q));
    if (annotationsStore.currentQuestions.filter((q) => q.status.isValid === false).length > 0) {
      return;
    }

    undoRedoHistory.addCommand(
      new SegmentationAnswerAddedCommand(
        annotationService,
        annotationsStore,
        freeDrawSegmentationService,
        annotationTypeBl,
        segmentation.feature.id,
        segmentation.questions.slice(),
        annotationsStore.currentQuestions.slice()
      )
    );

    segmentation.questions = annotationsStore.currentQuestions.map((x) => toJS(x));

    await clear();
  };

  const handleCancel = action(async (): Promise<void> => {
    await secureClear();
  });

  const secureClear = action(async (): Promise<void> => {
    if (await freeDrawSegmentationService.clearAsync()) {
      annotationService.deselectSegmentation();
    }
  });

  const clear = action(async (): Promise<void> => {
    freeDrawSegmentationService.clear();
    annotationService.deselectSegmentation();
    return Promise.resolve();
  });

  const cancelClear = action((): void => {
    freeDrawSegmentationService.cancelClear();
  });

  if (!annotationsStore.isSelectedSegWithQuestions && !annotationsStore.isSelectedSegOfFreeDrawType && !freeDrawSegmentationService.freeDrawInProgress) {
    return <></>;
  }

  return (
    <div className='questions-save'>
      {(!uiStore.isInValidation || annotationsStore.invalidQuestionsCount === 0) && (
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={() => {
              void handleCancel();
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={() => {
              void handleSave();
            }}
          >
            {t('save')}
          </Button>
        </>
      )}
      {uiStore.isInValidation && annotationsStore.invalidQuestionsCount > 0 && (
        <ColumnLayout size='sm'>
          <S_WarningFeedback>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className='warning-icon'
            />
            {t('invalid_questions_left', { count: annotationsStore.invalidQuestionsCount })}
          </S_WarningFeedback>
          <Button
            variant='outlined'
            onClick={() => {
              void handleSaveAndShowNext();
            }}
          >
            {t('save_and_show_next')}
          </Button>
        </ColumnLayout>
      )}
      <ConfirmationModal
        confirmationHeader={'clear_freedraw_confirmation_header'}
        confirmationQuestion={'clear_freedraw_confirmation_body'}
        onCancel={cancelClear}
        onConfirm={clear}
        showModal={freeDrawSegmentationService.showClearConfirmation}
        confirmationYes={'do_not_save'}
      />
    </div>
  );
});
